//
// # WorkspaceLayout
//
// The main layout structure for the IDE.
// Uses CSS Grid for the outer frame (title bar + dock area)
// and dockview for the resizable/dockable inner panels:
//
//   +-------------------------------------+
//   |            Title Bar                |
//   +----------+---------+----------------+
//   |          |         |                |
//   |  Left    | Editor  |     Right      |
//   |  Panel   | Area(s) |     Panel      |
//   |          |         |                |
//   +----------+---------+----------------+
//   |           Bottom Panel              |
//   +-------------------------------------+
//
// Editor tabs are managed as individual dockview panels, enabling native
// drag-to-reorder and drag-to-split (tiling) via the dockview library.
//

import React, { useCallback, useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import {
  DockviewReact,
  type DockviewReadyEvent,
  type IDockviewPanelProps,
  type IDockviewPanelHeaderProps,
} from "dockview-react";
import "dockview-react/dist/styles/dockview.css";
import {
  Search,
  PanelLeft,
  PanelRight,
  PanelBottom,
  MoreHorizontal,
} from "lucide-react";
import { EditorTabHeader, EditorTabPanel } from "./EditorTab";
import { PinDialog } from "./components/PinDialog";
import { EditWireLabelDialog } from "./components/EditWireLabelDialog";
import { EditRouteWidthDialog } from "./components/EditRouteWidthDialog";
import { RecoverSessionDialog } from "./components/RecoverSessionDialog";
import { EditTextAnnotationDialog } from "./components/EditTextAnnotationDialog";
import { InstanceDialog } from "./components/EditInstanceDialog/EditInstanceDialog";
import { DummyDeviceDialog } from "./components/DummyDeviceDialog/DummyDeviceDialog";
import { AiAssistant } from "./components/AiAssistant/AiAssistant";
import { CommandPalette } from "./components/CommandPalette/CommandPalette";
import { ConstraintsPanel } from "./components/ConstraintsPanel/ConstraintsPanel";
import { ConstraintDialog } from "./components/ConstraintsPanel/ConstraintDialog";
import { LvsPanel } from "./components/LvsPanel";
import { DrcPanel } from "./components/DrcPanel";
import { IvaPanel } from "./components/IvaPanel";
import { SrvPanel } from "./components/SrvPanel";
import { ParasiticsPanel } from "./components/ParasiticsPanel";
import { HelpPanel } from "./components/HelpPanel";
import { SaveAsDialog } from "./components/DesignExplorer/SaveAsDialog";
import { ReportIssueDialog } from "./components/ReportIssue/ReportIssueDialog";
import { DesignNameDialog } from "./components/DesignExplorer/DesignNameDialog";
import { RenameDuplicateDialog } from "./components/DesignExplorer/RenameDuplicateDialog";
import { DesignExplorer } from "./components/DesignExplorer/DesignExplorer";
import { LayerPanel } from "./components/LayerPanel/LayerPanel";
import { HierarchyPanel } from "./components/HierarchyPanel/HierarchyPanel";
import { PropertiesPanel } from "./components/PropertiesPanel/PropertiesPanel";
import { AgentHierarchyPanel } from "./components/AgentHierarchyPanel/AgentHierarchyPanel";
import { KeyboardHelpPanel } from "./components/KeyboardHelpPanel/KeyboardHelpPanel";
import { DeleteDesignDialog } from "./components/DeleteDesignDialog";
import { GenSymbolFromSchDialog } from "./components/symbol/GenSymbolFromSchDialog";
import { AddExprLabelDialog } from "./components/symbol/AddExprLabelDialog";
import { EditExprLabelDialog } from "./components/symbol/EditExprLabelDialog";
import { AddSymbolPinDialog } from "./components/symbol/AddSymbolPinDialog";
import { EditSymbolPinDialog } from "./components/symbol/EditSymbolPinDialog";
import { SchematicPickerDialog } from "./components/symbol/SchematicPickerDialog";
import { GraphicsCatalogueDialog } from "./components/symbol/GraphicsCatalogueDialog";
import { AddLayoutPinDialog } from "./components/AddLayoutPinDialog";
import { EditLayoutPinDialog } from "./components/EditLayoutPinDialog";
import { OverwriteDialog } from "./components/OverwriteDialog";
import { GenerateFromSourceDialog } from "./components/GenerateFromSourceDialog";
import { ManageLinkagesDialog } from "./components/ManageLinkagesDialog";
import { LayoutPickerDialog } from "./components/LayoutPickerDialog";
import { PowerRoutingDialog } from "./components/PowerRoutingDialog";
import { ReconnectDialog } from "./components/ReconnectDialog";
import { NetworkStatusIndicator } from "./components/NetworkStatusIndicator";
import { useWorkspace, useWorkspaceSnapshot } from "./context";
import { isMac } from "EditorCore";
import "./ide.css";

// Panel IDs that belong to sidebar groups. Used to constrain drag-and-drop
// so that sidebar panels stay in sidebar groups and editor panels stay in
// the editor area.
const SIDEBAR_PANEL_IDS = new Set([
  "left",
  "layers",
  "hierarchy",
  "right",
  "keyboard-help",
  "agents",
  "bottom",
  // Placeholder panels that keep sidebar groups alive as drop targets
  // when all real sidebar panels have been dragged elsewhere.
  "left-placeholder",
  "right-placeholder",
  "bottom-placeholder",
]);

function isSidebarPanel(panelId: string | null): boolean {
  return panelId !== null && SIDEBAR_PANEL_IDS.has(panelId);
}

// ------------------------------------------------------------------ //
// Custom tab components for dockview panels                           //
// ------------------------------------------------------------------ //

// Sidebar panel header: [≡] PANEL NAME [···]
// When collapsed, shows a narrow tab with just the first letter.
function SidebarTab({ api }: IDockviewPanelHeaderProps) {
  const workspace = useWorkspace();
  const { collapsedPanels } = useWorkspaceSnapshot();
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const panelId = api.id;
  const isCollapsed = collapsedPanels.has(panelId);

  // Close menu when clicking outside
  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  const handleCollapsePanel = () => {
    setMenuOpen(false);
    workspace.collapsePanel(panelId);
  };

  const handleCloseGroup = () => {
    setMenuOpen(false);
    // Hide all panels in the group by setting the group to not visible
    const group = api.group;
    if (group) {
      group.api.setVisible(false);
    }
  };

  const handleExpandPanel = (e: React.MouseEvent) => {
    e.stopPropagation();
    workspace.expandPanel(panelId);
  };

  const handleToggleMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!menuOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + 2,
        left: rect.right - 140, // menu width is 140px, align right edge
      });
    }
    setMenuOpen(!menuOpen);
  };

  // Collapsed state: show narrow tab with first letter
  if (isCollapsed) {
    const firstLetter = (api.title ?? "?")[0].toUpperCase();
    return (
      <div
        className="ide-collapsed-tab"
        title={`${api.title} (click to expand)`}
        onClick={handleExpandPanel}
      >
        <span className="ide-collapsed-tab-letter">{firstLetter}</span>
      </div>
    );
  }

  // Expanded state: full tab with title and more-actions
  return (
    <div className="flex items-center w-full h-full px-1 gap-1 select-none">
      <span className="flex-1 text-[11px] font-semibold uppercase tracking-widest text-[var(--color-ide-text-muted)] whitespace-nowrap px-1">
        {api.title}
      </span>
      <button
        ref={buttonRef}
        className="ide-panel-header-btn"
        title="More actions"
        onMouseDown={handleToggleMenu}
      >
        <MoreHorizontal size={13} />
      </button>
      {menuOpen &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            className="ide-panel-dropdown-menu"
            style={{
              position: "fixed",
              top: menuPos.top,
              left: menuPos.left,
            }}
          >
            <button className="ide-dropdown-item" onClick={handleCollapsePanel}>
              Collapse Panel
            </button>
            <button className="ide-dropdown-item" onClick={handleCloseGroup}>
              Close Group
            </button>
          </div>,
          document.body
        )}
    </div>
  );
}

// Invisible tab for the editor-area placeholder panel. This panel holds the
// center space in the layout; its tab is hidden via CSS using the marker class.
function EditorAreaTab(_props: IDockviewPanelHeaderProps) {
  return <div className="ide-editor-area-marker" />;
}

// Invisible tab for sidebar placeholder panels. These keep sidebar groups
// alive as valid drop targets even when all real panels have been dragged away.
function SidebarPlaceholderTab(_props: IDockviewPanelHeaderProps) {
  return <div className="ide-sidebar-placeholder-marker" />;
}

// Map of custom tab component IDs to React components.
const tabComponents: Record<string, React.FC<IDockviewPanelHeaderProps>> = {
  sidebar: SidebarTab,
  "editor-tab": EditorTabHeader,
  "editor-area": EditorAreaTab,
  "sidebar-placeholder": SidebarPlaceholderTab,
};

// ------------------------------------------------------------------ //
// Panel content components                                             //
// ------------------------------------------------------------------ //

// Panel component for the left sidebar — shows the Design Explorer tree.
function LeftPanel(_props: IDockviewPanelProps) {
  return <DesignExplorer />;
}

// Panel component for the bottom panel (console, messages, etc.).
// Renders the bounded ring-buffer of ConsoleLogEntry from WorkspaceSnapshot.
// Subsystems write to it via Workspace.logToConsole(); see main.tsx for the
// network event wiring. Auto-scrolls to the most recent entry on change.
function BottomPanel(_props: IDockviewPanelProps) {
  const { consoleLog } = useWorkspaceSnapshot();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Keep the view pinned to the newest entry. If the user has scrolled up
  // we still jump to bottom — acceptable because this panel is append-only
  // and doesn't support interactive inspection yet.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [consoleLog]);

  return (
    <div className="flex flex-col h-full bg-[var(--color-ide-surface)] text-[var(--color-ide-text)]">
      {/* Section header */}
      <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-widest text-[var(--color-ide-text-muted)] select-none border-b border-[var(--color-ide-border)]">
        Console Output
      </div>
      <div
        ref={scrollRef}
        className="flex-1 overflow-auto p-3 font-mono text-xs space-y-1"
      >
        {consoleLog.length === 0 ? (
          <p className="text-[var(--color-ide-text-muted)]">
            <span className="text-[var(--color-ide-text-dim)]">—</span> Ready.
          </p>
        ) : (
          consoleLog.map((entry) => (
            <ConsoleLogRow key={entry.id} entry={entry} />
          ))
        )}
      </div>
    </div>
  );
}

// Formats a Date.now() timestamp as HH:MM:SS.mmm in local time.
// Kept zero-padded so rows line up visually.
function formatConsoleTimestamp(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number, w = 2): string => String(n).padStart(w, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(
    d.getSeconds()
  )}.${pad(d.getMilliseconds(), 3)}`;
}

// Renders a single console row. Color is driven by the entry's level; source
// is rendered as a dim tag so it's scannable but not visually dominant.
function ConsoleLogRow({
  entry,
}: {
  entry: import("./types").ConsoleLogEntry;
}): React.ReactElement {
  const levelColor =
    entry.level === "error"
      ? "var(--color-ide-error)"
      : entry.level === "warn"
      ? "var(--color-ide-warn)"
      : entry.level === "debug"
      ? "var(--color-ide-text-dim)"
      : "var(--color-ide-text)";
  return (
    <p style={{ color: levelColor }}>
      <span className="text-[var(--color-ide-text-dim)]">
        {formatConsoleTimestamp(entry.timestamp)}
      </span>{" "}
      <span className="text-[var(--color-ide-text-muted)]">
        [{entry.source}]
      </span>{" "}
      {entry.message}
    </p>
  );
}

// Panel component for the right sidebar — AI assistant chat panel.
function RightPanel(_props: IDockviewPanelProps) {
  return <AiAssistant />;
}

// Panel component for the keyboard shortcuts panel — shows available shortcuts.
function KeyboardHelpPanelWrapper(_props: IDockviewPanelProps) {
  return <KeyboardHelpPanel />;
}

// Panel component for the layers panel — shows PDK layer visibility controls.
function LayerPanelWrapper(_props: IDockviewPanelProps) {
  return <LayerPanel />;
}

// Track whether a dockview panel is the visible tab in its group. Flips on
// tab switches and when the group is hidden. Lets a panel skip work while it's
// tucked behind another tab (dockview keeps hidden panels mounted).
function usePanelVisible(api: IDockviewPanelProps["api"]): boolean {
  const [visible, setVisible] = useState(api.isVisible);
  useEffect(() => {
    setVisible(api.isVisible);
    const disposable = api.onDidVisibilityChange((e) => {
      setVisible(e.isVisible);
    });
    return () => {
      disposable.dispose();
    };
  }, [api]);
  return visible;
}

// Panel component for the hierarchy panel — shows instance hierarchy tree.
// Passes tab visibility down so the tree skips its rebuild while hidden.
function HierarchyPanelWrapper(props: IDockviewPanelProps) {
  const visible = usePanelVisible(props.api);
  return <HierarchyPanel visible={visible} />;
}

// Panel component for the constraints panel — shows symmetry constraints.
function ConstraintsPanelWrapper(_props: IDockviewPanelProps) {
  return <ConstraintsPanel />;
}

// Panel component for the properties panel — shows/edits the selected entity.
function PropertiesPanelWrapper(_props: IDockviewPanelProps) {
  return <PropertiesPanel />;
}

// Panel component for the Agents panel — the live running-agent hierarchy.
// Passes tab visibility down so the panel pulls a fresh tree when shown.
function AgentHierarchyPanelWrapper(props: IDockviewPanelProps) {
  const visible = usePanelVisible(props.api);
  return <AgentHierarchyPanel visible={visible} />;
}

// Panel component for the LVS panel — shows LVS verification results.
function LvsPanelWrapper(_props: IDockviewPanelProps) {
  return <LvsPanel />;
}

// Panel component for the DRC panel — shows DRC verification results.
function DrcPanelWrapper(_: IDockviewPanelProps) {
  void _; // Required parameter signature but not used
  return <DrcPanel />;
}

// Panel component for the IVA panel — shows IVA verification results.
function IvaPanelWrapper(_props: IDockviewPanelProps) {
  return <IvaPanel />;
}

// Panel component for the SRV panel — shows schematic Schematic Rules Validation
// results. Passes the Dockview panel api through so the panel can forward its
// visibility to the SrvModel (which gates the canvas overlay).
function SrvPanelWrapper(props: IDockviewPanelProps) {
  return <SrvPanel api={props.api} />;
}

// Panel component for the Parasitics panel — shows PEX status and results.
function ParasiticsPanelWrapper(_props: IDockviewPanelProps) {
  return <ParasiticsPanel />;
}

// Panel component for the Help panel — shows searchable help documentation.
function HelpPanelWrapper(_props: IDockviewPanelProps) {
  return <HelpPanel />;
}

// Placeholder panel for the center editor area. Shown when no designs are open.
// This panel is permanent — it reserves the center space in the layout so that
// sidebar panels don't expand to fill it.
function EditorAreaPanel(_props: IDockviewPanelProps) {
  return (
    <div className="ide-editor-empty">
      <div style={{ textAlign: "center" }}>
        <div>No design open.</div>
        <br />
        <div>
          Use the Depot Explorer (left panel) to open or create a design.
        </div>
        <div>Press [{isMac ? "Cmd" : "Ctrl"}+P] for Command Palette.</div>
      </div>
    </div>
  );
}

// Placeholder content shown when a sidebar group has no real panels.
// Keeps the group alive as a valid drag-and-drop target.
function SidebarPlaceholderPanel(_props: IDockviewPanelProps) {
  return (
    <div className="ide-sidebar-placeholder">
      <span>Drag panels here</span>
    </div>
  );
}

// Dockview component registry - maps component IDs to React components.
const components: Record<string, React.FC<IDockviewPanelProps>> = {
  "editor-tab": EditorTabPanel,
  "editor-area": EditorAreaPanel,
  "sidebar-placeholder": SidebarPlaceholderPanel,
  left: LeftPanel,
  layers: LayerPanelWrapper,
  hierarchy: HierarchyPanelWrapper,
  bottom: BottomPanel,
  right: RightPanel,
  "keyboard-help": KeyboardHelpPanelWrapper,
  properties: PropertiesPanelWrapper,
  agents: AgentHierarchyPanelWrapper,
  constraints: ConstraintsPanelWrapper,
  lvs: LvsPanelWrapper,
  drc: DrcPanelWrapper,
  iva: IvaPanelWrapper,
  srv: SrvPanelWrapper,
  parasitics: ParasiticsPanelWrapper,
  help: HelpPanelWrapper,
};

// ------------------------------------------------------------------ //
// Main WorkspaceLayout                                                 //
// ------------------------------------------------------------------ //

export function WorkspaceLayout() {
  const workspace = useWorkspace();

  const {
    generateFromSourceDialog,
    overwriteDialog,
    commandPaletteOpen,
    designNameDialog,
    renameDuplicateDialog,
    genSymbolFromSchDialogOpen,
    graphicsCatalogueDialogOpen,
    saveAsDialogOpen,
    reportIssueDialogOpen,
    deleteDesignIdent,
    leftPanelVisible,
    rightPanelVisible,
    bottomPanelVisible,
    instanceDialog,
    schematicPickerDialog,
    layoutPickerDialog,
    addSymbolPinDialog,
    addExprLabelDialog,
    editSymbolPinDialog,
    editExprLabelDialog,
    addLayoutPinDialog,
    editLayoutPinDialog,
    pinDialog,
    editWireLabelDialog,
    editRouteWidthDialog,
    recoverSessionDialog,
    textAnnotationDialog,
    manageLinkagesDialog,
    constraintDialog,
    dummyDeviceDialog,
    powerRoutingDialog,
    reconnectDialog,
  } = useWorkspaceSnapshot();

  // Attach the CommandCenter keyboard listener on mount.
  // This handles Ctrl+S (save), Ctrl+P (open palette), and all
  // registered keybindings centrally rather than with scattered useEffects.
  useEffect(() => {
    workspace.commandCenter.attach();
    return () => workspace.commandCenter.detach();
  }, [workspace]);

  const onReady = useCallback(
    (event: DockviewReadyEvent) => {
      const api = event.api;

      // Build the initial layout. The editor-area placeholder panel holds
      // the center space; editor tab panels are added to its group later.

      // 1. Editor area placeholder (center, added first as the anchor)
      const editorArea = api.addPanel({
        id: "editor-area",
        component: "editor-area",
        title: "Editor",
        tabComponent: "editor-area",
      });

      // 2. Left sidebar, placed to the left of the editor area
      const leftPanel = api.addPanel({
        id: "left",
        component: "left",
        title: "Explorer",
        tabComponent: "sidebar",
        position: { referencePanel: editorArea, direction: "left" },
      });

      // 2b. Layers panel — tabbed in the same group as the left sidebar
      api.addPanel({
        id: "layers",
        component: "layers",
        title: "Layers",
        tabComponent: "sidebar",
        position: { referencePanel: leftPanel },
        inactive: true, // Explorer stays as default active tab
      });

      // 2c. Hierarchy panel — tabbed in the same group as the left sidebar
      api.addPanel({
        id: "hierarchy",
        component: "hierarchy",
        title: "Hierarchy",
        tabComponent: "sidebar",
        position: { referencePanel: leftPanel },
        inactive: true,
      });

      // 3. Right sidebar, placed to the right of the editor area
      const rightPanel = api.addPanel({
        id: "right",
        component: "right",
        title: "AI Assistant",
        tabComponent: "sidebar",
        position: { referencePanel: editorArea, direction: "right" },
      });

      // 3b. Keyboard Help panel — tabbed in the same group as the right sidebar
      api.addPanel({
        id: "keyboard-help",
        component: "keyboard-help",
        title: "Reference",
        tabComponent: "sidebar",
        position: { referencePanel: rightPanel },
        inactive: true, // AI Assistant stays as default active tab
      });

      // 3c. Properties panel — tabbed in the same group as the right sidebar.
      // Shows/edits the properties of the currently-selected entity.
      api.addPanel({
        id: "properties",
        component: "properties",
        title: "Properties",
        tabComponent: "sidebar",
        position: { referencePanel: rightPanel },
        inactive: true, // AI Assistant stays as default active tab
      });

      // 3d. Agents panel — the live running-agent hierarchy — tabbed in the
      // same group as the right sidebar.
      api.addPanel({
        id: "agents",
        component: "agents",
        title: "Agents",
        tabComponent: "sidebar",
        position: { referencePanel: rightPanel },
        inactive: true, // AI Assistant stays as default active tab
      });

      // 4. Bottom panel, below the editor area
      const bottomPanel = api.addPanel({
        id: "bottom",
        component: "bottom",
        title: "Console",
        tabComponent: "sidebar",
        position: { referencePanel: editorArea, direction: "below" },
      });

      // 4b. Constraints panel — tabbed in the same group as the bottom panel
      api.addPanel({
        id: "constraints",
        component: "constraints",
        title: "Constraints",
        tabComponent: "sidebar",
        position: { referencePanel: bottomPanel },
      });

      // 4c. LVS panel — tabbed in the same group as the bottom panel
      api.addPanel({
        id: "lvs",
        component: "lvs",
        title: "LVS",
        tabComponent: "sidebar",
        position: { referencePanel: bottomPanel },
      });

      // 4d. DRC panel — tabbed in the same group as the bottom panel
      api.addPanel({
        id: "drc",
        component: "drc",
        title: "DRC",
        tabComponent: "sidebar",
        position: { referencePanel: bottomPanel },
      });

      // 4d2. IVA panel — tabbed in the same group as the bottom panel
      api.addPanel({
        id: "iva",
        component: "iva",
        title: "IVA",
        tabComponent: "sidebar",
        position: { referencePanel: bottomPanel },
      });

      // 4d-srv. SRV panel — tabbed in the same group as the bottom panel
      api.addPanel({
        id: "srv",
        component: "srv",
        title: "Check & Save",
        tabComponent: "sidebar",
        position: { referencePanel: bottomPanel },
      });

      // 4e. Parasitics panel — tabbed in the same group as the bottom panel
      api.addPanel({
        id: "parasitics",
        component: "parasitics",
        title: "Parasitics",
        tabComponent: "sidebar",
        position: { referencePanel: bottomPanel },
      });

      // 4f. Help panel — tabbed in the same group as the bottom panel
      api.addPanel({
        id: "help",
        component: "help",
        title: "Help",
        tabComponent: "sidebar",
        position: { referencePanel: bottomPanel },
      });

      // 5. Placeholder panels for each sidebar group. These keep the groups
      //    alive as valid drop targets when all real sidebar panels have been
      //    dragged to other groups. Hidden via CSS like the editor-area tab.

      api.addPanel({
        id: "left-placeholder",
        component: "sidebar-placeholder",
        title: "",
        tabComponent: "sidebar-placeholder",
        position: { referencePanel: leftPanel },
        inactive: true,
      });
      api.addPanel({
        id: "right-placeholder",
        component: "sidebar-placeholder",
        title: "",
        tabComponent: "sidebar-placeholder",
        position: { referencePanel: rightPanel },
        inactive: true,
      });
      api.addPanel({
        id: "bottom-placeholder",
        component: "sidebar-placeholder",
        title: "",
        tabComponent: "sidebar-placeholder",
        position: { referencePanel: bottomPanel },
        inactive: true,
      });

      // Set initial proportions: left ~22%, right ~30%
      const totalWidth = api.width;
      const leftGroup = api.getPanel("left")?.group;
      const rightGroup = api.getPanel("right")?.group;
      const bottomGroup = api.getPanel("bottom")?.group;

      if (leftGroup) {
        api.getGroup(leftGroup.id)?.api.setSize({
          width: Math.round(totalWidth * 0.22),
        });
      }
      if (rightGroup) {
        api.getGroup(rightGroup.id)?.api.setSize({
          width: Math.round(totalWidth * 0.3),
        });
      }
      if (bottomGroup) {
        api.getGroup(bottomGroup.id)?.api.setSize({ height: 160 });
      }

      // Collect sidebar group IDs for drag-and-drop constraint checks.
      const sidebarGroupIds = new Set<string>();
      if (leftGroup) sidebarGroupIds.add(leftGroup.id);
      if (rightGroup) sidebarGroupIds.add(rightGroup.id);
      if (bottomGroup) sidebarGroupIds.add(bottomGroup.id);

      // Constrain drag-and-drop: sidebar panels can only be dropped into
      // sidebar groups, and editor panels can only be dropped into editor
      // groups. The onWillShowOverlay handler prevents the drop indicator
      // from appearing for invalid targets; onWillDrop is a safety net.
      const constrainDrop = (event: {
        getData: () => { panelId: string | null } | undefined;
        group?: { id: string } | undefined;
        kind: string;
        preventDefault: () => void;
      }) => {
        const data = event.getData();
        if (!data) return; // External drag (not from within dockview)

        const isSourceSidebar = isSidebarPanel(data.panelId);

        if (event.kind === "edge") {
          // Edge drops create new groups. Only allow for editor panels.
          if (isSourceSidebar) event.preventDefault();
          return;
        }

        const targetGroupId = event.group?.id;
        if (!targetGroupId) return;

        const isTargetSidebar = sidebarGroupIds.has(targetGroupId);
        if (isSourceSidebar !== isTargetSidebar) {
          event.preventDefault();
        }
      };

      api.onWillShowOverlay(constrainDrop);
      api.onWillDrop(constrainDrop);

      // Store the API and sidebar group IDs on the workspace for later use
      // (e.g., adding editor tabs, toggling sidebar group visibility).
      workspace.setDockviewApi(api, {
        left: leftGroup?.id ?? "",
        right: rightGroup?.id ?? "",
        bottom: bottomGroup?.id ?? "",
      });
    },
    [workspace]
  );

  return (
    <div className="ide-frame">
      {/* Top title bar: logo + nav arrows + command center + panel toggles */}
      <div className="ide-title-bar">
        <div className="ide-title-left">
          {/* GenAlpha logo mark */}
          <div className="ide-title-logo" title="GenAlpha IDE">
            α
          </div>
          <NetworkStatusIndicator />
        </div>
        <div
          className="ide-title-command-center"
          onClick={() => workspace.commandCenter.openPalette()}
        >
          <Search size={12} className="ide-title-command-icon" />
          <span>GenAlpha IDE</span>
        </div>
        <div className="ide-title-right">
          <button
            className={`ide-title-btn${
              leftPanelVisible ? " ide-title-btn-active" : ""
            }`}
            title="Toggle Left Panel"
            onClick={() => workspace.toggleSidebarGroup("left")}
          >
            <PanelLeft size={16} />
          </button>
          <button
            className={`ide-title-btn${
              bottomPanelVisible ? " ide-title-btn-active" : ""
            }`}
            title="Toggle Bottom Panel"
            onClick={() => workspace.toggleSidebarGroup("bottom")}
          >
            <PanelBottom size={16} />
          </button>
          <button
            className={`ide-title-btn${
              rightPanelVisible ? " ide-title-btn-active" : ""
            }`}
            title="Toggle Right Panel"
            onClick={() => workspace.toggleSidebarGroup("right")}
          >
            <PanelRight size={16} />
          </button>
        </div>
      </div>

      {/* Main dockview area */}
      <div className="ide-dock-area">
        <DockviewReact
          components={components}
          tabComponents={tabComponents}
          onReady={onReady}
          className="dockview-theme-abyss"
        />
      </div>

      {/* Generate From Source dialog (shown during Cmd+Shift+L) */}
      {generateFromSourceDialog && (
        <GenerateFromSourceDialog
          stacks={generateFromSourceDialog.stacks}
          defaultPath={generateFromSourceDialog.defaultPath}
          defaultOa={generateFromSourceDialog.defaultOa}
          onConfirm={(stackId, destination, rectangular) => {
            workspace.resolveGenerateFromSourceDialog({
              stackId,
              destination,
              rectangular,
            });
          }}
          onCancel={() => {
            workspace.resolveGenerateFromSourceDialog(null);
          }}
        />
      )}

      {/* Overwrite confirmation dialog */}
      {overwriteDialog && (
        <OverwriteDialog
          designPath={overwriteDialog.designPath}
          detail={overwriteDialog.detail}
          onOverwrite={() => workspace.resolveOverwriteDialog(true)}
          onCancel={() => workspace.resolveOverwriteDialog(false)}
        />
      )}

      {/* Schematic picker dialog (shown when symbol has multiple linked schematics) */}
      {schematicPickerDialog && (
        <SchematicPickerDialog
          schematics={schematicPickerDialog.schematics}
          entries={schematicPickerDialog.entries}
          sourcePdkId={schematicPickerDialog.sourcePdkId}
          onSelect={(ident) => workspace.selectSchematicFromPicker(ident)}
          onCancel={() => workspace.closeSchematicPickerDialog()}
        />
      )}

      {/* Layout picker dialog (shown when schematic has multiple linked layouts) */}
      {layoutPickerDialog && (
        <LayoutPickerDialog
          layouts={layoutPickerDialog.layouts}
          entries={layoutPickerDialog.entries}
          sourcePdkId={layoutPickerDialog.sourcePdkId}
          onSelect={(ident) => workspace.selectLayoutFromPicker(ident)}
          onCancel={() => workspace.closeLayoutPickerDialog()}
        />
      )}

      {/* Add Symbol Pin dialog */}
      {addSymbolPinDialog && (
        <AddSymbolPinDialog
          onConfirm={(config) => {
            workspace.closeAddSymbolPinDialog();
            workspace.confirmAddSymbolPin(
              addSymbolPinDialog.designIdent,
              addSymbolPinDialog.position,
              config
            );
          }}
          onCancel={() => workspace.closeAddSymbolPinDialog()}
        />
      )}

      {/* Add Expression Label dialog */}
      {addExprLabelDialog && (
        <AddExprLabelDialog
          onConfirm={(config) => {
            workspace.closeAddExprLabelDialog();
            workspace.confirmAddExprLabel(
              addExprLabelDialog.designIdent,
              addExprLabelDialog.position,
              config
            );
          }}
          onCancel={() => workspace.closeAddExprLabelDialog()}
        />
      )}

      {/* Edit Expression Label dialog */}
      {editExprLabelDialog && (
        <EditExprLabelDialog
          currentData={editExprLabelDialog.labelData}
          onConfirm={(config) => {
            workspace.closeEditExprLabelDialog();
            workspace.confirmEditExprLabel(
              editExprLabelDialog.designIdent,
              editExprLabelDialog.labelData.id,
              config
            );
          }}
          onCancel={() => {
            workspace.closeEditExprLabelDialog();
          }}
        />
      )}

      {/* Edit Symbol Pin dialog */}
      {editSymbolPinDialog && (
        <EditSymbolPinDialog
          currentData={editSymbolPinDialog.pinData}
          onConfirm={(config) => {
            workspace.closeEditSymbolPinDialog();
            const pinId = editSymbolPinDialog.pinData.id;
            if (pinId !== undefined) {
              workspace.confirmEditSymbolPin(
                editSymbolPinDialog.designIdent,
                pinId,
                config
              );
            }
          }}
          onCancel={() => workspace.closeEditSymbolPinDialog()}
        />
      )}

      {/* Add Layout Pin dialog */}
      {addLayoutPinDialog && (
        <AddLayoutPinDialog
          layers={addLayoutPinDialog.layers}
          onConfirm={(config) => {
            workspace.closeAddLayoutPinDialog();
            workspace.confirmAddLayoutPin(
              addLayoutPinDialog.designIdent,
              addLayoutPinDialog.position,
              config
            );
          }}
          onCancel={() => workspace.closeAddLayoutPinDialog()}
        />
      )}

      {/* Edit Layout Pin dialog */}
      {editLayoutPinDialog && (
        <EditLayoutPinDialog
          currentData={editLayoutPinDialog.pinData}
          layers={editLayoutPinDialog.layers}
          onConfirm={(config) => {
            workspace.closeEditLayoutPinDialog();
            workspace.confirmEditLayoutPin(
              editLayoutPinDialog.designIdent,
              editLayoutPinDialog.pinData.id,
              config
            );
          }}
          onCancel={() => workspace.closeEditLayoutPinDialog()}
        />
      )}

      {/* Command Palette overlay */}
      {commandPaletteOpen && (
        <CommandPalette
          commandCenter={workspace.commandCenter}
          onClose={() => workspace.commandCenter.closePalette()}
        />
      )}

      {/* New Design dialog — hoisted here so both DesignExplorer (the "+"
          button) and the command palette can trigger it via workspace
          methods. */}
      <DesignNameDialog
        mode={designNameDialog}
        onClose={() => {
          workspace.closeDesignNameDialog();
        }}
      />

      {/* Save As dialog */}
      <SaveAsDialog
        open={saveAsDialogOpen}
        onClose={() => workspace.closeSaveAsDialog()}
      />

      {/* Rename / Duplicate dialog (depot right-click menu) */}
      <RenameDuplicateDialog
        mode={renameDuplicateDialog}
        onClose={() => {
          workspace.closeRenameDuplicateDialog();
        }}
      />

      {/* Report-an-Issue dialog (see issue #2146) */}
      <ReportIssueDialog
        open={reportIssueDialogOpen}
        onClose={() => {
          workspace.closeReportIssueDialog();
        }}
      />

      {/* Generate Symbol from Schematic dialog */}
      <GenSymbolFromSchDialog
        open={genSymbolFromSchDialogOpen}
        onClose={() => workspace.closeGenSymbolFromSchDialog()}
      />

      {/* Graphics Catalogue dialog */}
      {graphicsCatalogueDialogOpen && (
        <GraphicsCatalogueDialog
          onConfirm={(entry) => {
            workspace.closeGraphicsCatalogueDialog();
            const editor = workspace.getActiveEditor();
            if (!editor || editor.content.contentKind !== "symbol") return;
            // Place at viewport center
            const { width, height } = editor.canvas.size;
            const center = editor.canvas.worldTransforms.toSch({
              x: width / 2,
              y: height / 2,
            });
            const elements = entry.create(center.x, center.y);
            editor.addCatalogueShape(elements);
          }}
          onCancel={() => workspace.closeGraphicsCatalogueDialog()}
        />
      )}

      {/* Delete Design confirmation dialog */}
      <DeleteDesignDialog
        ident={deleteDesignIdent}
        onClose={() => workspace.closeDeleteDesignDialog()}
      />

      {/* Instance dialog — add new or edit existing instance */}
      {instanceDialog && (
        <InstanceDialog
          state={instanceDialog}
          onClose={() => workspace.closeInstanceDialog()}
        />
      )}

      {/* Dummy device dialog — add dummy devices around instances */}
      {dummyDeviceDialog && (
        <DummyDeviceDialog
          state={dummyDeviceDialog}
          onClose={() => workspace.closeDummyDeviceDialog()}
        />
      )}

      {/* Pin dialog — covers new / editProperties / rename modes. */}
      <PinDialog
        open={pinDialog !== null}
        onClose={() => {
          workspace.closePinDialog();
        }}
      />

      {/* Edit Wire Label dialog */}
      <EditWireLabelDialog
        open={editWireLabelDialog !== null}
        onClose={() => {
          workspace.closeEditWireLabelDialog();
        }}
      />

      {/* Set Route Width dialog */}
      <EditRouteWidthDialog
        open={editRouteWidthDialog !== null}
        onClose={() => {
          workspace.closeEditRouteWidthDialog();
        }}
      />

      {/* Recover unsaved session dialog */}
      <RecoverSessionDialog
        open={recoverSessionDialog !== null}
        onClose={() => {
          workspace.closeRecoverSessionDialog();
        }}
      />

      {/* Add/Edit Text annotation dialog */}
      <EditTextAnnotationDialog
        open={textAnnotationDialog !== null}
        onClose={() => {
          workspace.closeTextAnnotationDialog();
        }}
      />

      {/* Constraint edit dialog */}
      <ConstraintDialog
        open={constraintDialog !== null}
        onClose={() => workspace.closeConstraintDialog()}
      />

      {/* Manage Linkages dialog */}
      {manageLinkagesDialog && (
        <ManageLinkagesDialog
          state={manageLinkagesDialog}
          onClose={() => workspace.closeManageLinkagesDialog()}
        />
      )}

      {/* Power Routing Configuration dialog */}
      {powerRoutingDialog && (
        <PowerRoutingDialog
          powerNets={powerRoutingDialog.powerNets}
          groundNets={powerRoutingDialog.groundNets}
          metalLayers={powerRoutingDialog.metalLayers}
          onConfirm={(config) => {
            const { designIdent } = powerRoutingDialog;
            workspace.closePowerRoutingDialog();
            void workspace.runPowerRouting(designIdent, config);
          }}
          onCancel={() => workspace.closePowerRoutingDialog()}
        />
      )}

      {/* Connection Lost dialog — non-dismissible, only resolution is reload */}
      <ReconnectDialog state={reconnectDialog} />
    </div>
  );
}
